import { CREATOR_MEMBERSHIP_MAX_POST_IMAGES } from './membership-types';

export const CREATOR_MEMBERSHIP_POST_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const CREATOR_MEMBERSHIP_MAX_POST_EMBEDS = 1;
export const CREATOR_MEMBERSHIP_MAX_POST_PDFS = 1;

const ALLOWED_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]);

export type MembershipPostEmbedProvider = 'youtube' | 'loom';

export type MembershipPostAttachmentInput =
  | {
      kind: 'image';
      key: string;
      name?: string;
      mimeType: string;
      sizeBytes: number;
    }
  | {
      kind: 'pdf';
      key: string;
      name: string;
      mimeType: string;
      sizeBytes: number;
    }
  | {
      kind: 'embed';
      url: string;
    };

export type MembershipPostAttachment =
  | {
      kind: 'image';
      url: string;
      key: string;
      name: string;
      mimeType: string;
      sizeBytes: number;
    }
  | {
      kind: 'pdf';
      url: string;
      key: string;
      name: string;
      mimeType: string;
      sizeBytes: number;
    }
  | {
      kind: 'embed';
      provider: MembershipPostEmbedProvider;
      url: string;
      embedUrl: string;
    };

export function parseMembershipPostEmbedUrl(
  value: string,
): { ok: true; provider: MembershipPostEmbedProvider; url: string; embedUrl: string } | { ok: false; message: string } {
  const trimmed = value.trim();
  if (!trimmed) return { ok: false, message: 'Embed URL is required' };

  try {
    const url = new URL(trimmed);
    const host = url.hostname.toLowerCase();

    if (host === 'youtu.be') {
      const videoId = url.pathname.replace(/^\//, '').split('/')[0];
      if (!videoId) return { ok: false, message: 'Invalid YouTube URL' };
      const canonical = `https://www.youtube.com/watch?v=${videoId}`;
      return {
        ok: true,
        provider: 'youtube',
        url: canonical,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
      };
    }

    if (host.endsWith('youtube.com')) {
      const videoId =
        url.searchParams.get('v') ??
        (url.pathname.startsWith('/embed/') ? url.pathname.split('/')[2] : null) ??
        (url.pathname.startsWith('/shorts/') ? url.pathname.split('/')[2] : null);
      if (!videoId) return { ok: false, message: 'Invalid YouTube URL' };
      const canonical = `https://www.youtube.com/watch?v=${videoId}`;
      return {
        ok: true,
        provider: 'youtube',
        url: canonical,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
      };
    }

    if (host.endsWith('loom.com')) {
      const shareMatch = url.pathname.match(/^\/share\/([^/]+)/);
      if (shareMatch?.[1]) {
        const embedUrl = `https://www.loom.com/embed/${shareMatch[1]}`;
        return {
          ok: true,
          provider: 'loom',
          url: trimmed,
          embedUrl,
        };
      }
      if (url.pathname.startsWith('/embed/')) {
        return {
          ok: true,
          provider: 'loom',
          url: trimmed,
          embedUrl: trimmed,
        };
      }
      return { ok: false, message: 'Invalid Loom URL' };
    }
  } catch {
    return { ok: false, message: 'Invalid embed URL' };
  }

  return { ok: false, message: 'Only YouTube and Loom embeds are supported' };
}

function isAttachmentInput(value: unknown): value is MembershipPostAttachmentInput {
  if (!value || typeof value !== 'object') return false;
  const kind = (value as { kind?: unknown }).kind;
  return kind === 'image' || kind === 'pdf' || kind === 'embed';
}

export function validateMembershipPostAttachments(
  attachments: unknown,
): { ok: true; normalized: MembershipPostAttachmentInput[] } | { ok: false; message: string } {
  if (attachments == null) return { ok: true, normalized: [] };
  if (!Array.isArray(attachments)) return { ok: false, message: 'Attachments must be an array' };

  const normalized: MembershipPostAttachmentInput[] = [];
  let imageCount = 0;
  let embedCount = 0;
  let pdfCount = 0;

  for (const item of attachments) {
    if (!isAttachmentInput(item)) {
      return { ok: false, message: 'Invalid attachment payload' };
    }

    if (item.kind === 'image') {
      imageCount += 1;
      if (imageCount > CREATOR_MEMBERSHIP_MAX_POST_IMAGES) {
        return {
          ok: false,
          message: `Updates can include up to ${CREATOR_MEMBERSHIP_MAX_POST_IMAGES} images`,
        };
      }
      const mimeType = item.mimeType?.trim();
      if (!mimeType || !ALLOWED_IMAGE_MIME_TYPES.has(mimeType)) {
        return { ok: false, message: 'Unsupported image type' };
      }
      if (!item.key?.trim()) {
        return { ok: false, message: 'Each image must include a confirmed upload key' };
      }
      if (item.sizeBytes > CREATOR_MEMBERSHIP_POST_FILE_MAX_BYTES) {
        return { ok: false, message: 'Each image must be 25 MB or smaller' };
      }
      normalized.push({
        kind: 'image',
        key: item.key.trim(),
        name: item.name?.trim() || 'Image',
        mimeType,
        sizeBytes: item.sizeBytes,
      });
      continue;
    }

    if (item.kind === 'pdf') {
      pdfCount += 1;
      if (pdfCount > CREATOR_MEMBERSHIP_MAX_POST_PDFS) {
        return { ok: false, message: 'Updates can include one PDF attachment' };
      }
      const mimeType = item.mimeType?.trim();
      if (mimeType !== 'application/pdf') {
        return { ok: false, message: 'PDF attachments must be application/pdf' };
      }
      if (!item.key?.trim()) {
        return { ok: false, message: 'PDF attachments must include a confirmed upload key' };
      }
      if (!item.name?.trim()) {
        return { ok: false, message: 'PDF attachments require a file name' };
      }
      if (item.sizeBytes > CREATOR_MEMBERSHIP_POST_FILE_MAX_BYTES) {
        return { ok: false, message: 'PDF attachments must be 25 MB or smaller' };
      }
      normalized.push({
        kind: 'pdf',
        key: item.key.trim(),
        name: item.name.trim(),
        mimeType,
        sizeBytes: item.sizeBytes,
      });
      continue;
    }

    embedCount += 1;
    if (embedCount > CREATOR_MEMBERSHIP_MAX_POST_EMBEDS) {
      return { ok: false, message: 'Updates can include one video embed' };
    }
    const parsed = parseMembershipPostEmbedUrl(item.url);
    if (!parsed.ok) return parsed;
    normalized.push({ kind: 'embed', url: parsed.url });
  }

  return { ok: true, normalized };
}

export function normalizeMembershipPostEmbedAttachment(
  input: Extract<MembershipPostAttachmentInput, { kind: 'embed' }>,
): Extract<MembershipPostAttachment, { kind: 'embed' }> {
  const parsed = parseMembershipPostEmbedUrl(input.url);
  if (!parsed.ok) throw new Error(parsed.message);
  return {
    kind: 'embed',
    provider: parsed.provider,
    url: parsed.url,
    embedUrl: parsed.embedUrl,
  };
}
